import React, { useEffect, useMemo, useState } from "react";
import { Eye } from "lucide-react";
import { parseStream } from "../../lib/streamParse";

const clock = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

const useElapsed = (since, on) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!on) return undefined;
    setNow(Date.now());
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [on, since]);
  return since ? Math.max(0, Math.floor((now - new Date(since).getTime()) / 1000)) : 0;
};

const Progress = ({ last, busy }) => {
  const secs = useElapsed(last?.created_at, busy);
  const { files, current } = useMemo(() => parseStream(last?.raw || ""), [last?.raw]);
  if (!busy || !last) return null;
  const done = files.filter((f) => f.done).length;
  const plan = last.mode === "plan" || last.kind === "plan";
  return (
    <span className="ml-auto flex items-center gap-1.5 shrink-0 font-mono text-xs text-[#2F6FD1]/80" data-testid="agent-status-progress">
      <span data-testid="agent-status-timer">{clock(secs)}</span>
      {!plan && <span data-testid="agent-status-files">· {done} file{done === 1 ? "" : "s"}</span>}
      {!plan && current && <span className="hidden sm:inline max-w-[160px] truncate" data-testid="agent-status-current">· writing {current.path}</span>}
      {last.continue_credits > 0 && <span data-testid="agent-status-extra">· +{last.continue_credits}cr</span>}
    </span>
  );
};

const statusOf = (busy, stopping, last) => {
  if (busy && stopping) return ["Stopping…", "busy"];
  if (busy && last?.stage === "images") return ["Agent is creating images…", "busy"];
  if (busy && last?.stage === "continuing") return [`Agent is continuing a long build (part ${(last.continuations || 1) + 1})…`, "busy"];
  if (busy && (last?.mode === "plan" || last?.kind === "plan")) return ["Agent is planning…", "busy"];
  if (busy) return ["Agent is building…", "busy"];
  if (last?.status === "error") return ["Agent hit a problem — send your message again", "error"];
  if (last?.kind === "plan" && last?.status === "done") return ["Agent is waiting for you to approve the plan…", "idle"];
  return ["Agent is waiting…", "idle"];
};

const TONE = {
  busy: ["bg-[#E8F0FB] text-[#2F6FD1]", "bg-[#2F6FD1]", "animate-ping"],
  idle: ["bg-[#E8F0FB] text-[#2F6FD1]", "bg-[#2F6FD1]", ""],
  error: ["bg-rose-50 text-rose-700", "bg-rose-500", ""],
};

export const AgentStatus = ({ busy, stopping, last }) => {
  const [label, tone] = statusOf(busy, stopping, last);
  const [bar, dot, ping] = TONE[tone];
  return (
    <div className={`flex items-center gap-3 px-4 py-2.5 rounded-t-2xl text-sm font-medium ${bar}`} data-testid="agent-status-bar" data-state={tone}>
      <span className="relative flex w-6 h-6 items-center justify-center rounded-full bg-white/60 shrink-0">
        {ping && <span className={`absolute w-2.5 h-2.5 rounded-full ${dot} opacity-60 ${ping}`} />}
        <span className={`relative w-2.5 h-2.5 rounded-full ${dot}`} />
      </span>
      <span className="truncate" data-testid="agent-status-text">{label}</span>
      <Progress last={last} busy={busy} />
    </div>
  );
};

export const ViewPreviewButton = ({ onClick, desktop }) => (
  <div className={`${desktop ? "flex" : "flex lg:hidden"} justify-center pb-3 pointer-events-none`}>
    <button type="button" onClick={onClick} data-testid="view-preview-button"
      className="pointer-events-auto h-11 px-5 rounded-2xl bg-white border border-slate-200 shadow-[0_6px_20px_-8px_rgba(15,23,42,0.25)] text-slate-800 font-semibold text-[15px] flex items-center gap-2.5 hover:-translate-y-0.5 hover:shadow-[0_10px_24px_-10px_rgba(15,23,42,0.35)] active:translate-y-0 transition-[transform,box-shadow]">
      <Eye size={19} strokeWidth={2.2} /> View Preview
    </button>
  </div>
);

export default AgentStatus;
